import ZoomVideo, { AudioChangeAction, ConnectionState, MutedSource, VideoQuality } from '@zoom/videosdk';
import { callFirebaseFunction } from 'boot/firebase';
import { chain, get, has, isEmpty } from 'lodash';
import { mapActions, mapState } from 'pinia';
import { uid } from 'quasar';
import { IS_INVITATION_ACCEPTED } from 'src/constants/videoSession';
import BillingDB from 'src/fb/billing-db';
import VideoSessionsDB from 'src/fb/videoSessions-db';
import Interactions from 'src/mixins/Interactions';
import { auth } from 'src/store/auth';
import { currentVideoSession } from 'src/store/currentVideoSession';
import { sessions } from 'src/store/sessions';
import { userRoles } from 'src/utils/AppUtils';
import { logEvent } from 'src/utils/LogEvent';

const VIDEO_ACTIONS = {
  START: 'Start',
  STOP: 'Stop',
};

const ZOOM_EVENTS = {
  USER_ADDED: 'user-added',
  USER_REMOVED: 'user-removed',
  USER_UPDATED: 'user-updated',
  CURRENT_AUDIO_CHANGED: 'current-audio-change',
  HOST_ASK_UNMUTE_AUDIO: 'host-ask-unmute-audio',
  ACTIVE_SPEAKER: 'active-speaker',
  CHAT_ON_MESSAGE: 'chat-on-message',
  CONNECTION_CHANGE: 'connection-change',
  NETWORK_QUALITY_CHANGE: 'network-quality-change',
  PEER_VIDEO_STATE_CHANGE: 'peer-video-state-change',
};

const FAILOVER_THROTTLE = 2 * 60 * 1000;
const WARNING_THROTTLE = 30 * 1000;

export default {

  mixins: [Interactions],

  data() {
    return {
      loading: {
        chat: false,
        finishVideoSession: false,
      },

      isHide: false,
      isShowVideoCallPanel: false,
      videoSessionId: null,

      activeZoomStream: null,
      activeZoomChat: null,
      zoomVideoClient: null,
      myselfId: null,
      isReconnecting: false,
      failoverWarningTimestamp: 0,
      networkWarningTimestamp: 0,
    };
  },

  emits: ['finish-session'],

  computed: {
    ...mapState(auth, ['userDetails']),
    ...mapState(sessions, ['videoSessionMembersNames', 'currentVideoCallSession', 'isInvitedUser']),
    ...mapState(currentVideoSession, ['stateZoomToken', 'stateZoomVideoClient', 'stateActiveZoomStream']),

    canUserInviteToCall() {
      const defaultRoles = [userRoles.MANAGER, userRoles.ADMINISTRATOR, userRoles.SUPERVISOR];

      if (!get(this.activeSession, 'videoSessionLimitedVisibility', false)) {
        return defaultRoles.includes(this.userDetails.role);
      }

      const allowedRoles = [...defaultRoles, ...get(this.activeSession, 'videoSessionVisibility', [])];

      return allowedRoles.includes(this.userDetails.role);
    }, // canUserInviteToCall()

    isNativeAndroid() {
      return this.$q.platform.is.capacitor && this.$q.platform.is.android;
    }, // isNativeAndroid()

    isVideoSessionCreator() {
      return get(this.currentVideoCallSession, 'runBy', '') === this.userDetails.userId;
    }, // isVideoSessionCreator()

    waitingForUserText() {
      if (!this.currentVideoCallSession || isEmpty(get(this, ['currentVideoCallSession', 'members'], []))) {
        return this.$t('sessions.videoSession.textWaitingForConnect');
      }

      const otherMember = chain(this.currentVideoCallSession.members)
        .filter((id) => id !== this.userDetails.userId)
        .head()
        .value();

      return this.$t(`sessions.videoSession.${otherMember
        ? 'textWaitingForConnectWithName'
        : 'textWaitingForConnect'}`, {
        userName: this.videoSessionMembersNames[otherMember],
      });
    }, // waitingForUserText()

    addClassLocalVideo() {
      return this.$q.platform.is.capacitor && (this.$q.platform.is.ios || this.$q.platform.is.android)
        ? 'mobile-local-video'
        : 'local-video';
    },

    addClassRemoteVideo() {
      return this.$q.platform.is.capacitor && (this.$q.platform.is.ios || this.$q.platform.is.android)
        ? 'mobile-remote-video'
        : 'remote-video';
    },
  },

  methods: {
    ...mapActions(sessions, ['setVideoSessionMembersNames', 'getActiveVideoSession']),
    ...mapActions(currentVideoSession, [
      'setStateZoomToken',
      'setStateZoomVideoClient',
      'setStateActiveZoomStream',
      'setIsRemoteParticipantConnected',
      'setIsRemoteParticipantOffAudio',
      'setIsRemoteParticipantOffCamera',
      'setCamera',
      'setMicrophone',
      'resetVideoSession',
    ]),

    async showRoom(invitedUserId) {
      const isSupported = ZoomVideo.checkSystemRequirements().video && ZoomVideo.checkSystemRequirements().audio;

      if (isSupported) {
        this.isShowVideoCallPanel = true;
        this.isHide = true;
        this.createZoomChat(invitedUserId);
        return;
      }

      this.openBrowserNotSupportVideoCallDialog();
    }, // showRoom()

    async leaveZoomVideoCall(method, force, isHostReconnectFailed) {
      if (!this.activeZoomStream) {
        return;
      }

      const participants = this.zoomVideoClient.getAllUser();
      const currentUser = this.zoomVideoClient.getCurrentUserInfo();

      if (this.isInvitedUser || isHostReconnectFailed) {
        const leavingParticipant = participants.find(
          (participant) => participant.displayName === this.userDetails.name,
        );

        if (leavingParticipant) {
          await this.renderVideo({ action: VIDEO_ACTIONS.STOP, userId: leavingParticipant?.userId });

          this.zoomVideoClient.off('peer-video-state-change', this.renderVideo);
          await this.zoomVideoClient.leave();
        }
      } else {
        // eslint-disable-next-line no-restricted-syntax
        for (const participant of participants) {
          // eslint-disable-next-line no-await-in-loop
          await this.renderVideo({ action: VIDEO_ACTIONS.STOP, userId: participant.userId });
        }
        this.zoomVideoClient.off('peer-video-state-change', this.renderVideo);
        await this.zoomVideoClient.leave(currentUser?.isHost);

        await this.finishVideoSession(force);
        sessionStorage.removeItem(IS_INVITATION_ACCEPTED);
      }

      this.$emit('finish-session');
      this.activeZoomStream = null;
      this.resetVideoSession();
      await ZoomVideo.destroyClient();
    },

    async toggleZoomVideo() {
      const stream = this.zoomVideoClient.getMediaStream();
      if (stream.isCapturingVideo()) {
        await stream.stopVideo();
        await this.renderVideo({
          action: VIDEO_ACTIONS.STOP,
          userId: this.zoomVideoClient.getCurrentUserInfo().userId,
        });
      } else {
        await stream.startVideo();
        await this.renderVideo({
          action: VIDEO_ACTIONS.START,
          userId: this.zoomVideoClient.getCurrentUserInfo().userId,
        });
      }
      this.setCamera(stream.isCapturingVideo());
    }, // toggleZoomVideo

    async toggleZoomAudio() {
      const stream = this.zoomVideoClient.getMediaStream();
      if (this.zoomVideoClient.getCurrentUserInfo().muted) {
        await stream.unmuteAudio();
      } else {
        await stream.muteAudio();
        const localVideoElement = document.querySelector(`.${this.addClassLocalVideo}`);
        localVideoElement?.classList.remove('active-speaker');
      }
      this.setMicrophone(!stream.isAudioMuted(this.myselfId));
    }, // toggleZoomAudio

    async toggleZoomRemoteAudio() {
      if (!this.zoomVideoClient.getCurrentUserInfo().isHost) {
        return;
      }
      const participants = this.zoomVideoClient.getAllUser();
      const remoteParticipant = participants.find((user) => !user.isHost);
      if (!remoteParticipant) {
        return;
      }

      const stream = this.zoomVideoClient.getMediaStream();
      if (remoteParticipant.muted) {
        await stream.unmuteAudio(remoteParticipant.userId);
        this.setIsRemoteParticipantOffAudio(false);
      } else {
        await stream.muteAudio(remoteParticipant.userId);
        this.setIsRemoteParticipantOffAudio(true);
        const remoteVideoElement = document.querySelector(`.${this.addClassRemoteVideo}`);
        remoteVideoElement?.classList.remove('active-speaker');
      }
    }, // toggleZoomRemoteAudio

    async controlRemoteParticipantVideoCall(type) {
      if (!this.zoomVideoClient.getCurrentUserInfo().isHost) {
        return;
      }
      const participants = this.zoomVideoClient.getAllUser();
      const remoteParticipant = participants.find((user) => !user.isHost);
      if (!remoteParticipant) {
        return;
      }

      await this.activeZoomChat.send(type, remoteParticipant.userId);
    }, // controlRemoteParticipantVideoCall

    async updateDevices(camera, microphone, speaker) {
      const stream = this.zoomVideoClient.getMediaStream();

      if (camera && camera.deviceId) {
        await stream.switchCamera(camera.deviceId);
      }

      if (microphone && microphone.deviceId) {
        await stream.switchMicrophone(microphone.deviceId);
      }

      if (speaker && speaker.deviceId) {
        await stream.switchSpeaker(speaker.deviceId);
      }
    }, // updateDevices()

    async createZoomChat(invitedUserId) {
      this.loading.chat = true;

      let roomName = '';

      const activeCurrentVideoSession = await this.getActiveVideoSession();

      const videoSessionId = activeCurrentVideoSession?.id;

      if (videoSessionId) {
        this.videoSessionId = videoSessionId;
        roomName = activeCurrentVideoSession.roomName;
      } else if (this.isInvitedUser) {
        roomName = this.currentVideoCallSession.roomName;
        this.videoSessionId = this.currentVideoCallSession.id;
      } else {
        roomName = `${uid()}-${Date.now()}`;
        const videoSession = await this.createVideoSession(roomName, invitedUserId);
        this.videoSessionId = videoSession.id;
      }

      let zoomToken;
      try {
        if (!this.stateZoomToken) {
          zoomToken = await callFirebaseFunction('generateZoomToken', {
            role: this.isInvitedUser ? 'participant' : 'host',
            sessionName: roomName,
          });
          this.setStateZoomToken(zoomToken);
        } else {
          zoomToken = this.stateZoomToken;
        }

        if (!zoomToken) {
          throw new Error(this.$t('sessions.videoSession.emptyTokenError'));
        }
      } catch (error) {
        const errorMessage = this.$t('sessions.videoSession.notificationAboutRoomTokenGenerationError', {
          error: error.errorMessage || error.reason,
        });

        console.error(errorMessage);
        this.notifyNegative(errorMessage);
        logEvent(error);
        this.loading.chat = false;
        return;
      }

      if (!this.stateZoomVideoClient) {
        this.zoomVideoClient = ZoomVideo.createClient();
        this.setStateZoomVideoClient(this.zoomVideoClient);

        await this.zoomVideoClient.init('en-US', 'Global', {
          patchJsMedia: true,
          leaveOnPageUnload: true,
          stayAwake: true,
        });

        this.zoomVideoClient.on(ZOOM_EVENTS.PEER_VIDEO_STATE_CHANGE, this.renderVideo);

        try {
          await this.zoomVideoClient.join(roomName, zoomToken.data, this.userDetails.name);
        } catch (err) {
          const errorMessage = this.$t('sessions.videoSession.notificationAboutRoomJoinError', {
            error: err.errorMessage || err.reason,
          });

          console.error(errorMessage);
          this.notifyNegative(errorMessage);
          logEvent(err.reason);
          return;
        }

        this.activeZoomStream = this.zoomVideoClient.getMediaStream();
        this.setStateActiveZoomStream(this.activeZoomStream);

        try {
          await this.activeZoomStream.startAudio();
        } catch (err) {
          this.notifyNegative(this.$t('sessions.videoSession.notificationAboutStartAudioError', {
            error: err.errorMessage || err.reason,
          }));
          logEvent(err.errorMessage || err.reason);
          this.setMicrophone(false);
        }

        try {
          await this.activeZoomStream.startVideo({ originalRatio: false });
        } catch (err) {
          this.notifyNegative(this.$t('sessions.videoSession.notificationAboutStartVideoError', {
            error: err.message || err.reason,
          }));
          logEvent(err.message || err.reason);
          this.setCamera(false);
        }
      } else {
        this.zoomVideoClient = this.stateZoomVideoClient;
        this.zoomVideoClient.on(ZOOM_EVENTS.PEER_VIDEO_STATE_CHANGE, this.renderVideo);

        this.activeZoomStream = this.stateActiveZoomStream;
      }

      this.activeZoomChat = this.zoomVideoClient.getChatClient();
      this.myselfId = this.zoomVideoClient.getCurrentUserInfo()?.userId;

      const participants = this.zoomVideoClient.getAllUser();
      const remoteParticipant = participants.find(
        (user) => user.userId !== this.myselfId,
      );

      this.setIsRemoteParticipantConnected(!!remoteParticipant);

      if (remoteParticipant && this.stateZoomVideoClient) {
        await this.renderVideo({ action: VIDEO_ACTIONS.START, userId: remoteParticipant.userId });
        this.setIsRemoteParticipantOffCamera(!remoteParticipant.bVideoOn);
      }

      if (remoteParticipant && this.zoomVideoClient.getCurrentUserInfo()?.isHost) {
        this.setIsRemoteParticipantOffAudio(remoteParticipant.muted);
      }

      await this.renderVideo({
        action: VIDEO_ACTIONS.START, userId: this.zoomVideoClient.getCurrentUserInfo()?.userId,
      });

      this.zoomVideoClient.on(ZOOM_EVENTS.USER_ADDED, this.handleUserAdded);

      this.zoomVideoClient.on(ZOOM_EVENTS.USER_REMOVED, this.handleUserRemoved);

      this.failoverWarningTimestamp = 0;
      this.zoomVideoClient.on(
        ZOOM_EVENTS.USER_UPDATED,
        (payload) => this.handleUserUpdated(payload, this.zoomVideoClient),
      );

      this.zoomVideoClient.on(ZOOM_EVENTS.CURRENT_AUDIO_CHANGED, this.handleCurrentAudioChange);

      this.zoomVideoClient.on(ZOOM_EVENTS.HOST_ASK_UNMUTE_AUDIO, this.handleHostAskUnmuteAudio);

      this.zoomVideoClient.on(ZOOM_EVENTS.ACTIVE_SPEAKER, this.handleActiveSpeaker);

      // Use this event to control remote participant video call
      this.zoomVideoClient.on(ZOOM_EVENTS.CHAT_ON_MESSAGE, this.handleChatOnMessage);

      this.zoomVideoClient.on(ZOOM_EVENTS.CONNECTION_CHANGE, this.handleConnectionChange);

      this.networkWarningTimestamp = 0;
      this.zoomVideoClient.on(ZOOM_EVENTS.NETWORK_QUALITY_CHANGE, this.handleNetworkQualityChange);
    },

    handleUserAdded(payload) {
      payload.forEach((item) => {
        if (item.displayName === this.userDetails.name && this.myselfId !== item.userId) {
          this.myselfId = item.userId;
        }

        if (item.displayName !== this.userDetails.name && this.myselfId !== item.userId) {
          this.setIsRemoteParticipantConnected(true);
        }
      });
    },

    handleUserRemoved(payload) {
      payload.forEach(() => {
        this.setIsRemoteParticipantConnected(false);
      });
    },

    async handleUserUpdated(payload, client) {
      await Promise.all(payload.map(async (item) => {
        if (has(item, 'bVideoOn') && item.userId !== this.myselfId) {
          this.setIsRemoteParticipantOffCamera(!item.bVideoOn);
        }

        if (has(item, 'muted') && !this.isInvitedUser) {
          if (item.userId !== this.myselfId) {
            this.setIsRemoteParticipantOffAudio(item.muted);
          } else {
            this.setMicrophone(!item.muted);
          }
        }

        const updatedUserInfo = client.getUser(item.userId);

        // If the host cannot reconnect due to internet issues, we must end the call
        // for the participant in the UI and in the database.
        if (updatedUserInfo?.isInFailover
          && !updatedUserInfo?.bVideoOn
          && item.userId !== this.myselfId
          && this.isInvitedUser) {
          this.notifyParticipantAboutChatChanges(
            this.$t('sessions.videoSession.notificationAboutDisconnectHostConnectionFailed'),
          );
          await this.leaveZoomVideoCall();
          await this.finishVideoSession(true);
          sessionStorage.removeItem(IS_INVITATION_ACCEPTED);
        }

        const now = Date.now();

        if (updatedUserInfo?.isInFailover && item.userId !== this.myselfId
          && (now - this.failoverWarningTimestamp > FAILOVER_THROTTLE)) {
          this.notifyNegative(this.$t(
            'sessions.videoSession.notificationAboutRemoteUserInFailover',
            { userName: updatedUserInfo.displayName },
          ));
          this.failoverWarningTimestamp = now;
        }

        // After reconnecting due to network issues, we need to re-render the local video
        if (item.bVideoOn && this.isReconnecting && item.userId === this.myselfId) {
          await this.renderVideo({ action: VIDEO_ACTIONS.START, userId: item.userId });
          this.isReconnecting = false;
        }
      }));
    },

    handleCurrentAudioChange(payload) {
      const { action, source } = payload;

      if (action === AudioChangeAction.Muted && source === MutedSource.PassiveByMuteOne) {
        this.setIsRemoteParticipantOffAudio(true);

        const localVideoElement = document.querySelector(`.${this.addClassLocalVideo}`);
        localVideoElement?.classList.remove('active-speaker');

        this.notifyParticipantAboutChatChanges(this.$t('sessions.videoSession.notificationAboutMuteAudio'));
      }

      if (action === AudioChangeAction.Unmuted) {
        this.setIsRemoteParticipantOffAudio(false);
      }
    },

    async handleHostAskUnmuteAudio() {
      await this.toggleZoomAudio();
      this.setIsRemoteParticipantOffAudio(false);
      this.notifyParticipantAboutChatChanges(this.$t('sessions.videoSession.notificationAboutUnmuteAudio'));
    },

    handleActiveSpeaker(payload) {
      const [{ userId }] = payload;

      const localVideoElement = document.querySelector(`.${this.addClassLocalVideo}`);
      const remoteVideoElement = document.querySelector(`.${this.addClassRemoteVideo}`);

      if (this.myselfId === userId) {
        remoteVideoElement?.classList.remove('active-speaker');
        localVideoElement?.classList.add('active-speaker');
      } else {
        localVideoElement?.classList.remove('active-speaker');
        remoteVideoElement?.classList.add('active-speaker');
      }
    },

    async handleChatOnMessage(payload) {
      const { message, sender } = payload;

      if (!this.isInvitedUser || sender.userId === this.myselfId) {
        return;
      }

      if (message === 'disconnect') {
        await this.leaveZoomVideoCall();
        sessionStorage.removeItem(IS_INVITATION_ACCEPTED);
        this.notifyParticipantAboutChatChanges(this.$t('sessions.videoSession.notificationAboutDisconnect'));
      }

      if (message === 'video_on') {
        await this.toggleZoomVideo();
        this.notifyParticipantAboutChatChanges(this.$t('sessions.videoSession.notificationAboutTurnOnVideo'));
      }

      if (message === 'video_off') {
        await this.toggleZoomVideo();
        this.notifyParticipantAboutChatChanges(this.$t('sessions.videoSession.notificationAboutTurnOffVideo'));
      }
    },

    async handleConnectionChange(payload) {
      const { state, reason } = payload;

      if (state === ConnectionState.Closed) {
        if (reason === 'ended by host') {
          if (this.isInvitedUser) {
            this.notifyParticipantAboutChatChanges(this.$t('sessions.videoSession.notificationAboutDisconnect'));
            sessionStorage.removeItem(IS_INVITATION_ACCEPTED);
            await this.leaveZoomVideoCall();
          }

          if (this.$q.platform.is.ios || this.$q.platform.is.android) {
            this.$emit('finish-session');
          }
        }
      } else if (state === ConnectionState.Reconnecting) {
        this.isReconnecting = true;
      } else if (state === ConnectionState.Connected) {
        this.notifySuccess(this.$t('sessions.videoSession.notificationAboutConnect'));
      } else if (state === ConnectionState.Fail) {
        await this.leaveZoomVideoCall(null, null, !this.isInvitedUser);
        sessionStorage.removeItem(IS_INVITATION_ACCEPTED);
        this.notifyWarning(
          this.isInvitedUser
            ? this.$t('sessions.videoSession.notificationAboutFailedReconnect')
            : this.$t('sessions.videoSession.notificationAboutHostFailedReconnect'),
        );
      }
    },

    handleNetworkQualityChange(payload) {
      const { level } = payload;
      const now = Date.now();

      if (level < 3) {
        // Only show warning if enough time passed since the last one
        if (now - this.networkWarningTimestamp > WARNING_THROTTLE) {
          this.notifyWarning(this.$t('sessions.videoSession.notificationAboutWeakConnection'));
          this.networkWarningTimestamp = now;
        }
      }
    },

    async renderVideo(event) {
      const stream = this.zoomVideoClient.getMediaStream();
      const connectedUser = this.zoomVideoClient.getAllUser().find((user) => user.userId === event.userId);

      if (event.action === VIDEO_ACTIONS.STOP) {
        const element = await stream.detachVideo(event.userId);
        if (Array.isArray(element)) {
          element.forEach((el) => el?.remove());
        } else {
          element?.remove();
        }
      } else if (event.action === VIDEO_ACTIONS.START && connectedUser?.bVideoOn) {
        const connectedParticipantVideo = await stream.attachVideo(event.userId, VideoQuality.Video_720P);

        if (this.userDetails.name === connectedUser.displayName) {
          const localVideoElement = document.querySelector(`.${this.addClassLocalVideo}`);
          localVideoElement?.remove();

          connectedParticipantVideo.classList.add(this.addClassLocalVideo);
        } else {
          const remoteVideoElement = document.querySelector(`.${this.addClassRemoteVideo}`);
          remoteVideoElement?.remove();

          connectedParticipantVideo.classList.add(this.addClassRemoteVideo);
          this.setIsRemoteParticipantConnected(true);
        }

        const container = document.querySelector('video-player-container');
        if (container) {
          container.appendChild(connectedParticipantVideo);
        }
      }
    },

    async createVideoSession(roomName, invitedUserId) {
      const videoSessionsDB = new VideoSessionsDB();
      return videoSessionsDB.create({
        roomName,
        runBy: this.userDetails.userId,
        sessionId: this.activeSession.id,
        members: [invitedUserId, this.userDetails.userId],
        status: 'active',
        clinic: this.userDetails.clinic,
        clientId: this.currentClient.id || this.activeSession.client,
        platform: 'web',
      });
    }, // createVideoSession()

    finishVideoSession(force) {
      if (!this.isVideoSessionCreator && !force) {
        return;
      }

      if (this.isVideoSessionCreator) {
        this.setVideoSessionMembersNames({});
      }

      return callFirebaseFunction('finishVideoSession', {
        videoSessionId: this.videoSessionId,
      });
    }, // finishVideoSession()

    openAccessPermissionDeniedDialog() {
      this.$q.dialog({
        title: this.$t('sessions.videoSession.dialogWarnTitle'),
        message: this.$t('sessions.videoSession.textEnableCameraAndMicrophone'),
        cancel: true,
        persistent: false,
      });
    }, // openAccessPermissionDeniedDialog()

    openBrowserNotSupportVideoCallDialog() {
      this.$q.dialog({
        title: this.$t('sessions.videoSession.dialogWarnTitle'),
        message: this.$t('sessions.videoSession.textBrowserNotSupportedVideoCall'),
        cancel: true,
        persistent: false,
      });
    }, // openBrowserNotSupportVideoCallDialog()

    getBillingPeriodInfo() {
      const billingDB = new BillingDB();
      return billingDB.read(this.currentClient.clinic);
    }, // getBillingPeriodInfo()

    async countClientUsedMinutesForCurrentBilling() {
      const billingInfo = await this.getBillingPeriodInfo();
      const usedMinutesHistory = await this.getVideoCallHistory();
      return usedMinutesHistory.data[billingInfo.currentBillingPeriodStart]
        .reduce((accumulator, currentValue) => accumulator + currentValue.value);
    }, // countClientUsedMinutesForCurrentBilling()

    getVideoCallHistory() {
      return callFirebaseFunction('getVideoCallHistory', {
        clinic: this.currentClient.clinic,
        client: this.currentClient.id,
      });
    }, // getVideoCallHistory()

    notifyParticipantAboutChatChanges(message) {
      return this.$q.notify({
        type: 'info',
        progress: true,
        message,
      });
    }, // notifyParticipantAboutChatChanges()
  },
};
